// ============================================================================
// FSA NOTE MODAL — OVERVIEW PANE
// ============================================================================
// Landing pane when the modal first opens. Surfaces totals, reconciliation
// status, last-saved provenance. Read-only.

import React from 'react';
import type { NoteHydrate, NoteWorkspaceData } from 'api/types/fsa/noteData.types';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';

interface Props {
  hydrate: NoteHydrate;
  workspace: NoteWorkspaceData;
  isDirty: boolean;
  /** FSA ICAI NCE Owners' Capital — live re-route an appropriation to a section. */
  setAppropriationRouting?: (col: string, section: string) => void;
}

const SECTION_LABEL_FALLBACK: Record<string, string> = {
  PC_CAPITAL_ACCOUNT: "Partner's Capital Account",
  PC_CURRENT_ACCOUNT: "Partner's Current Account",
};

/** Builds the appropriation-routing view (columns + destination sections +
 *  effective selection) from the hydrate + workspace override. Null when the
 *  note declares no `appropriation_routing` (every note except Owners' Capital). */
function parseApprRoutingView(hydrate: NoteHydrate, workspace: NoteWorkspaceData) {
  let def: Record<string, string> = {};
  try {
    const md = JSON.parse(hydrate.scheduleMetaDataJson ?? '{}') as { appropriation_routing?: Record<string, string> };
    def = md?.appropriation_routing ?? {};
  } catch { def = {}; }
  const codes = Object.keys(def);
  if (codes.length === 0) return null;

  let cols: FormatColumnSpec[] = [];
  try { cols = JSON.parse(hydrate.columnSpecJson ?? '[]') as FormatColumnSpec[]; } catch { cols = []; }
  const labelByCode: Record<string, string> = {};
  for (const c of cols) labelByCode[c.code] = c.print_label || c.label || c.code;

  const eff = { ...def, ...(workspace.appropriation_routing ?? {}) };
  const sections = (hydrate.liveClasses ?? [])
    .filter(c => { try { return (JSON.parse(c.metaDataJson ?? '{}') as { partner_lines?: boolean })?.partner_lines === true; } catch { return false; } })
    .map(c => ({ code: c.classCode, name: c.className || SECTION_LABEL_FALLBACK[c.classCode] || c.classCode }));
  if (sections.length < 2) return null;

  return {
    items: codes.map(code => ({ code, label: labelByCode[code] ?? code, current: eff[code] ?? def[code] })),
    sections,
  };
}

const OverviewPane: React.FC<Props> = ({ hydrate, workspace, isDirty, setAppropriationRouting }) => {
  const totals = computeTotals(workspace);
  const recon = computeReconciliation(hydrate, totals);
  const factorLabel = hydrate.roundingFactorKey ?? 'ACTUALS';
  const saved = hydrate.workspace;
  const apprView = parseApprRoutingView(hydrate, workspace);

  return (
    <div style={contentStyle}>
      <SectionTitle>Summary</SectionTitle>
      <div style={summaryGrid}>
        <SummaryCard
          label="Closing (CY)"
          actual={totals.cActual}
          rounded={totals.cRounded}
          factorLabel={factorLabel}
        />
        <SummaryCard
          label="Opening (PY)"
          actual={totals.oActual}
          rounded={totals.oRounded}
          factorLabel={factorLabel}
        />
        <ReconciliationCard recon={recon} />
        <StatusCard saved={saved} isDirty={isDirty} />
      </div>

      {apprView && setAppropriationRouting && (
        <>
          <SectionTitle>Appropriation Routing</SectionTitle>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.4 }}>
            Choose where each appropriation posts — Partner&apos;s Capital or Current Account.
            Firms that maintain a separate Current Account route remuneration, interest and
            profit share through it; others post directly to Capital. Changes apply immediately.
          </div>
          <table style={refTableStyle}>
            <tbody>
              {apprView.items.map(it => (
                <tr key={it.code}>
                  <td style={refLabelStyle}>{it.label}</td>
                  <td style={refValueStyle}>
                    <select
                      className="form-input"
                      value={it.current}
                      onChange={e => setAppropriationRouting(it.code, e.target.value)}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', maxWidth: 280 }}
                    >
                      {apprView.sections.map(s => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <SectionTitle>Note Reference</SectionTitle>
      <table style={refTableStyle}>
        <tbody>
          <tr>
            <td style={refLabelStyle}>Code</td>
            <td style={refValueStyle}>{hydrate.scheduleCode}</td>
          </tr>
          <tr>
            <td style={refLabelStyle}>Name</td>
            <td style={refValueStyle}>{hydrate.scheduleName}</td>
          </tr>
          <tr>
            <td style={refLabelStyle}>Under Report</td>
            <td style={refValueStyle}>
              {hydrate.reportCode} — {hydrate.reportName}
            </td>
          </tr>
          <tr>
            <td style={refLabelStyle}>Rounding Factor</td>
            <td style={refValueStyle}>{factorLabel}</td>
          </tr>
          <tr>
            <td style={refLabelStyle}>Tolerance</td>
            <td style={refValueStyle}>{hydrate.roundingTolerance.toFixed(2)}</td>
          </tr>
          <tr>
            <td style={refLabelStyle}>Classes</td>
            <td style={refValueStyle}>{hydrate.liveClasses.length} class(es)</td>
          </tr>
          <tr>
            <td style={refLabelStyle}>Ledgers Mapped</td>
            <td style={refValueStyle}>
              {hydrate.liveClasses.reduce((s, c) => s + c.ledgerCount, 0)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ───── helpers ───────────────────────────────────────────────────────────────

function computeTotals(workspace: NoteWorkspaceData) {
  let cActual = 0, cRounded = 0, oActual = 0, oRounded = 0;
  for (const cd of Object.values(workspace.class_data)) {
    cActual += cd.amount_actual;
    cRounded += cd.amount_rounded;
    oActual += cd.prior_amount_actual;
    oRounded += cd.prior_amount_rounded;
  }
  return { cActual, cRounded, oActual, oRounded };
}

function computeReconciliation(
  hydrate: NoteHydrate,
  totals: { cActual: number; oActual: number },
) {
  let liveSum = 0;
  for (const c of hydrate.liveClasses) liveSum += c.liveClosingActual;
  const diff = totals.cActual - liveSum;
  const tolerance = hydrate.roundingTolerance ?? 1.0;
  return {
    liveSum,
    workspaceSum: totals.cActual,
    diff,
    matches: Math.abs(diff) <= tolerance,
  };
}

const fmtAmt = (v: number) =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ───── sub-components ────────────────────────────────────────────────────────

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={sectionTitleStyle}>{children}</h3>
);

const SummaryCard: React.FC<{
  label: string;
  actual: number;
  rounded: number;
  factorLabel: string;
}> = ({ label, actual, rounded, factorLabel }) => (
  <div style={cardStyle}>
    <div style={cardLabelStyle}>{label}</div>
    <div style={cardValueStyle}>₹ {fmtAmt(actual)}</div>
    <div style={cardSubValueStyle}>
      Rounded ({factorLabel}): {fmtAmt(rounded)}
    </div>
  </div>
);

const ReconciliationCard: React.FC<{
  recon: { liveSum: number; workspaceSum: number; diff: number; matches: boolean };
}> = ({ recon }) => (
  <div style={cardStyle}>
    <div style={cardLabelStyle}>TB Reconciliation</div>
    <div
      style={{
        ...cardValueStyle,
        color: recon.matches ? '#16a34a' : '#d97706',
        fontSize: '0.95rem',
      }}
    >
      {recon.matches ? '✓ Matches TB' : '⚠ Drift'}
    </div>
    <div style={cardSubValueStyle}>
      Live TB: ₹ {fmtAmt(recon.liveSum)}
      {!recon.matches && <> · diff ₹ {fmtAmt(recon.diff)}</>}
    </div>
  </div>
);

const StatusCard: React.FC<{
  saved: NoteHydrate['workspace'];
  isDirty: boolean;
}> = ({ saved, isDirty }) => (
  <div style={cardStyle}>
    <div style={cardLabelStyle}>Status</div>
    <div
      style={{
        ...cardValueStyle,
        fontSize: '0.95rem',
        color: isDirty ? '#d97706' : '#16a34a',
      }}
    >
      {!saved ? 'Never Saved' : isDirty ? 'Unsaved Changes' : `${saved.statusCode}`}
    </div>
    <div style={cardSubValueStyle}>
      {saved?.updatedAt
        ? `Last saved ${new Date(saved.updatedAt).toLocaleString()}${
            saved.updatedByName ? ` by ${saved.updatedByName}` : ''
          }`
        : 'Not yet persisted to server'}
    </div>
  </div>
);

// ───── styles ────────────────────────────────────────────────────────────────

const contentStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  marginTop: 16,
  marginBottom: 10,
};

const summaryGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 12,
  marginBottom: 8,
};

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  padding: '10px 14px',
  background: 'var(--bg-primary)',
};

const cardLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  marginBottom: 4,
};

const cardValueStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--text-primary)',
  marginBottom: 4,
};

const cardSubValueStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
};

const refTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const refLabelStyle: React.CSSProperties = {
  padding: '6px 12px',
  width: 160,
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-color)',
  fontWeight: 500,
};
const refValueStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
};

export default OverviewPane;
